import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { type AttackEvent, type MapPoint, historyResponseSchema } from '@repo/shared';
import { CLASSIFICATION_COLOR } from '../lib/format';
import { WORLD_PATH, WORLD_VIEWBOX, project } from './world-path';
import styles from './WorldMap.module.css';

/** How many recent geolocated attacks to plot. */
const MAX_PINS = 60;

/** Which slice of history the map is showing. */
type MapMode = 'live' | 'historical';

/** Zoom limits, expressed as how far the viewBox may shrink from full extent. */
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

/** Graticule (lat/lon grid) every 30° — the viewBox is a plain equirectangular 1000x500. */
const GRATICULE_PATH = (() => {
  const { width, height } = WORLD_VIEWBOX;
  const segs: string[] = [];
  for (let i = 1; i < 12; i++) segs.push(`M${((i * width) / 12).toFixed(1)} 0V${height}`);
  for (let i = 1; i < 6; i++) segs.push(`M0 ${((i * height) / 6).toFixed(1)}H${width}`);
  return segs.join('');
})();

interface Pin {
  id: number;
  x: number;
  y: number;
  color: string;
}

interface HistPin {
  id: number;
  x: number;
  y: number;
  /** Node radius in map units, scaled by how busy the origin is. */
  r: number;
  count: number;
  color: string;
}

/** The visible viewBox window: `x y w h` in map units. */
interface View {
  x: number;
  y: number;
  w: number;
  h: number;
}

const FULL_VIEW: View = { x: 0, y: 0, w: WORLD_VIEWBOX.width, h: WORLD_VIEWBOX.height };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Keep the window inside the map bounds so you can't pan off into the void. */
function clampView(v: View): View {
  const w = clamp(v.w, WORLD_VIEWBOX.width / MAX_ZOOM, WORLD_VIEWBOX.width / MIN_ZOOM);
  const h = w * (WORLD_VIEWBOX.height / WORLD_VIEWBOX.width);
  const x = clamp(v.x, 0, WORLD_VIEWBOX.width - w);
  const y = clamp(v.y, 0, WORLD_VIEWBOX.height - h);
  return { x, y, w, h };
}

/**
 * 2D equirectangular world map. Recent geolocated attacks glow as pins; the
 * newest ones ping outward. Chosen over a 3D globe deliberately — it's lighter
 * and reads instantly inside a portfolio iframe. Pan by dragging, zoom with the
 * wheel or the on-map controls.
 */
export function WorldMap({ events }: { events: AttackEvent[] }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [view, setView] = useState<View>(FULL_VIEW);
  // Mirror of `view` for the native wheel handler: reading through a ref keeps
  // `toMap` (and so the listener effect) stable across zoom/pan renders, so the
  // non-passive listener is attached once instead of churning on every step.
  const viewRef = useRef(view);
  viewRef.current = view;
  const drag = useRef<{ pointerId: number; startX: number; startY: number; view: View } | null>(
    null,
  );
  const [dragging, setDragging] = useState(false);

  const [mode, setMode] = useState<MapMode>('live');
  const [history, setHistory] = useState<MapPoint[] | null>(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  const zoom = WORLD_VIEWBOX.width / view.w;

  const pins = useMemo<Pin[]>(() => {
    const out: Pin[] = [];
    for (const e of events) {
      if (e.lat == null || e.lon == null) continue;
      const { x, y } = project(e.lon, e.lat);
      out.push({ id: e.id, x, y, color: CLASSIFICATION_COLOR[e.classification] });
      if (out.length >= MAX_PINS) break;
    }
    return out;
  }, [events]);

  // Pull the all-time origin aggregate whenever historical mode is (re)entered,
  // so the view reflects the latest ring buffer each time it's opened.
  useEffect(() => {
    if (mode !== 'historical') return;
    let cancelled = false;
    setHistoryLoading(true);
    void fetch('/api/history')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        const parsed = historyResponseSchema.safeParse(data);
        if (!parsed.success || cancelled) return;
        setHistory(parsed.data.points);
        setHistoryTotal(parsed.data.total);
      })
      .catch(() => {
        /* keep whatever we had; live mode is unaffected */
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const histPins = useMemo<HistPin[]>(() => {
    if (!history || history.length === 0) return [];
    const max = history.reduce((m, p) => Math.max(m, p.count), 1);
    return history.map((p, i) => {
      const { x, y } = project(p.lon, p.lat);
      // sqrt scale so a 100x-busier origin reads as ~10x the radius, not 100x.
      const t = Math.sqrt(p.count) / Math.sqrt(max);
      return {
        id: i,
        x,
        y,
        r: 2.5 + t * 6,
        count: p.count,
        color: CLASSIFICATION_COLOR[p.classification],
      };
    });
  }, [history]);

  /** Map a client-space point to map-units within the current viewBox. */
  const toMap = useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const v = viewRef.current;
    return {
      x: v.x + ((clientX - rect.left) / rect.width) * v.w,
      y: v.y + ((clientY - rect.top) / rect.height) * v.h,
    };
  }, []);

  /** Zoom by `factor` while keeping the map point under (fx, fy) fixed. */
  const zoomAt = useCallback((factor: number, fx: number, fy: number) => {
    setView((v) => {
      const next = clampView({
        x: fx - (fx - v.x) / factor,
        y: fy - (fy - v.y) / factor,
        w: v.w / factor,
        h: v.h / factor,
      });
      return next;
    });
  }, []);

  // Native, non-passive listener on the whole card so we can preventDefault the
  // page scroll everywhere over the map — including the controls and the
  // letterboxed margins — so wheel focus never bounces back to the window.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const pt = toMap(e.clientX, e.clientY);
      if (!pt) return;
      // Proportional, gently-damped zoom: trackpads fire many small events, so
      // scale by the (clamped) scroll delta rather than a fixed step per event.
      const delta = clamp(e.deltaY, -40, 40);
      const factor = Math.exp(-delta * 0.0025);
      zoomAt(factor, pt.x, pt.y);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [toMap, zoomAt]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<SVGSVGElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, view };
      setDragging(true);
    },
    [view],
  );

  const onPointerMove = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dx = ((e.clientX - d.startX) / rect.width) * d.view.w;
    const dy = ((e.clientY - d.startY) / rect.height) * d.view.h;
    setView(clampView({ ...d.view, x: d.view.x - dx, y: d.view.y - dy }));
  }, []);

  const endDrag = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    if (drag.current?.pointerId === e.pointerId) {
      drag.current = null;
      setDragging(false);
    }
  }, []);

  const zoomButton = useCallback(
    (factor: number) => zoomAt(factor, view.x + view.w / 2, view.y + view.h / 2),
    [zoomAt, view],
  );

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <svg
        ref={svgRef}
        className={styles.svg}
        style={{ cursor: dragging ? 'grabbing' : 'grab' }}
        viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="World map of recent attack origins. Drag to pan, scroll to zoom."
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <path className={styles.graticule} d={GRATICULE_PATH} />
        <path className={styles.land} d={WORLD_PATH} />
        {mode === 'live' ? (
          <g>
            {pins.map((p, i) => (
              <g key={p.id} transform={`translate(${p.x} ${p.y})`}>
                {/* Only the freshest handful ping, to keep motion calm. */}
                {i < 6 && <circle className={styles.ping} r={5} style={{ stroke: p.color }} />}
                {/* Soft colored halo makes the node pop off the land. */}
                <circle
                  className={styles.halo}
                  r={6}
                  style={{ fill: p.color, opacity: Math.max(0.12, 0.35 - i / (MAX_PINS * 2)) }}
                />
                {/* Dark contrast ring + bright core so nodes read at any zoom. */}
                <circle className={styles.dotRing} r={4} />
                <circle
                  className={styles.dot}
                  r={3.4}
                  style={{
                    fill: p.color,
                    color: p.color,
                    opacity: Math.max(0.5, 1 - i / MAX_PINS),
                  }}
                />
              </g>
            ))}
          </g>
        ) : (
          // Historical: static, filter-free nodes (there can be hundreds) sized
          // by attack volume. No pings — this is a settled all-time picture.
          <g>
            {histPins.map((p) => (
              <g key={p.id} transform={`translate(${p.x} ${p.y})`}>
                <circle
                  className={styles.histHalo}
                  r={p.r + 3}
                  style={{ fill: p.color, opacity: 0.22 }}
                />
                <circle className={styles.dotRing} r={p.r} />
                <circle
                  className={styles.histDot}
                  r={Math.max(1.2, p.r - 0.6)}
                  style={{ fill: p.color }}
                />
              </g>
            ))}
          </g>
        )}
      </svg>

      <div className={styles.modes} role="group" aria-label="Map data range">
        <button
          type="button"
          className={`${styles.mode} ${mode === 'live' ? styles.modeActive : ''}`}
          onClick={() => setMode('live')}
          aria-pressed={mode === 'live'}
        >
          live
        </button>
        <button
          type="button"
          className={`${styles.mode} ${mode === 'historical' ? styles.modeActive : ''}`}
          onClick={() => setMode('historical')}
          aria-pressed={mode === 'historical'}
        >
          all-time
        </button>
      </div>

      {mode === 'historical' && (
        <div className={styles.caption}>
          {historyLoading && history == null
            ? 'loading history…'
            : `${histPins.length} origins · ${historyTotal.toLocaleString()} attacks`}
        </div>
      )}

      <div className={styles.controls} role="group" aria-label="Map zoom">
        <button
          type="button"
          className={styles.ctrl}
          onClick={() => zoomButton(1.4)}
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          className={styles.ctrl}
          onClick={() => zoomButton(1 / 1.4)}
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          className={styles.ctrl}
          onClick={() => setView(FULL_VIEW)}
          aria-label="Reset zoom"
          disabled={zoom <= MIN_ZOOM + 0.001}
        >
          ⟳
        </button>
      </div>
    </div>
  );
}
